import React, { useState, useRef } from "react";
import moment from "moment";
import axios from "axios";
import { FiFileText, FiCheckCircle, FiStar, FiUpload, FiCheck, FiX } from "react-icons/fi";
import { FaFacebook, FaGoogle } from "react-icons/fa";
import { generateSignature } from "../../../redux/apiCalls";
import PosFieldLabel from "../../../components/shared/PosFieldLabel";

const SurveyTab = ({
    surveyData,
    orderData,
    handleSurveyChange,
    signatureRef,
    setSignatureRef,
    clearSignature,
    handleSignatureEnd,
    saveSurvey,
    generateSurveyPDFOnly,
    isSavingSurvey,
    superadmin,
    staff,
    installer,
    installerManager,
    isRegularUser,
    handleReviewStatusChange,
}) => {
    const isInstallerRole = installer || installerManager;
    const isEditingAllowed = superadmin || staff || isRegularUser || (orderData?.status !== "completed" && !isInstallerRole);

    const getReviewStatusBadge = (status) => {
        switch (status) {
            case "approved":
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FiCheck className="w-3 h-3 mr-1" />
                        Approved
                    </span>
                );
            case "rejected":
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <FiX className="w-3 h-3 mr-1" />
                        Rejected
                    </span>
                );
            case "pending":
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pending
                    </span>
                );
        }
    };

    // State for hover preview
    const [hoveredStar, setHoveredStar] = useState(0);
    const [isUploadingFacebook, setIsUploadingFacebook] = useState(false);
    const [isUploadingGoogle, setIsUploadingGoogle] = useState(false);
    const facebookInputRef = useRef(null);
    const googleInputRef = useRef(null);

    const handleReviewImageUpload = async (file, type, isReplacement = false) => {
        if (!file) return null;
        
        const setUploading = type === "facebook" ? setIsUploadingFacebook : setIsUploadingGoogle;
        setUploading(true);
        
        try {
            const imageResult = await generateSignature();
            const imageFormData = new FormData();
            const fileName = `${Date.now()}_${file.name}`;
            
            imageFormData.append("file", file);
            imageFormData.append("publicKey", imageResult.data.publicKey);
            imageFormData.append("signature", imageResult.data.signature);
            imageFormData.append("expire", imageResult.data.expire);
            imageFormData.append("token", imageResult.data.token);
            imageFormData.append("fileName", `${fileName} - ${imageResult.data.expire}`);
            imageFormData.append("folder", "reviews");
            
            const uploadResult = await axios.post(
                "https://upload.imagekit.io/api/v1/files/upload",
                imageFormData
            );
            
            const url = uploadResult.data.url;
            if (type === "facebook") {
                handleSurveyChange("facebookReviewUrl", url);
            } else {
                handleSurveyChange("googleReviewUrl", url);
            }
            
            if (isReplacement && handleReviewStatusChange) {
                handleReviewStatusChange(type, "pending", true, url);
            }
            
            return url;
        } catch (error) {
            console.error(`Error uploading ${type} review image:`, error);
            alert(`Failed to upload image. Please try again.`);
            return null;
        } finally {
            setUploading(false);
        }
    };

    // Format address from individual fields
    const formattedAddress = [
        orderData?.lineOne,
        orderData?.lineTwo,
        orderData?.zip,
        orderData?.city,
        orderData?.country
    ].filter(Boolean).join(", ");
    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {/* Enhanced Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Customer Satisfaction Survey
                        </h1>
                        <p className="text-gray-600 text-sm">
                            Collect customer feedback
                        </p>
                    </div>
                </div>
            </div>

            {/* Enhanced Introduction Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-200">
                <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                            <FiStar className="w-6 h-6 text-white" />
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Dear Valued Client,</h3>
                        <p className="text-gray-700 leading-relaxed">
                            <span className="font-bold text-blue-600">INHOME GROUP SDN BHD</span> strives to provide our
                            clients with the highest possible level of services and projects. In order for us to 
                            continuously improve our services, could you spare some time and participate in our
                            Satisfaction Survey by giving us your views on the following criteria:
                        </p>
                    </div>
                </div>
            </div>

            {/* Enhanced Rating Section */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 mb-8">
                <div className="text-white py-4 px-6" style={{ backgroundColor: '#f6ad55' }}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold flex items-center gap-2 flex-wrap">
                            <FiStar className="h-5 w-5" />
                            Service Rating Criteria
                        </h3>
                        <p className="text-sm opacity-90">Please select one rating for each criteria</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr className="bg-gray-100">
                            <th
                                rowSpan={2}
                                className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700"
                            >
                                Criteria
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                Excellent
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                Good
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                Average
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                Poor
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                Unacceptable
                            </th>
                        </tr>
                        <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                5
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                4
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                3
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                2
                            </th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                                1
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {[
                            { key: "pricing", label: "1. Pricing" },
                            {
                                key: "projectCompletion",
                                label: "2. Project Completion",
                            },
                            {
                                key: "qualityOfWork",
                                label: "3. Quality of work",
                            },
                            {
                                key: "communication",
                                label: "4. Communication",
                            },
                            {
                                key: "maintenance",
                                label: "5. Maintenance",
                            },
                        ].map((criteria, index) => (
                            <tr
                                key={criteria.key}
                                className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                }
                            >
                                <td className="border border-gray-300 px-4 py-3 font-medium text-gray-700">
                                    {criteria.label}
                                </td>
                                {[5, 4, 3, 2, 1].map((rating) => (
                                    <td
                                        key={rating}
                                        className="border border-gray-300 px-4 py-3 text-center"
                                    >
                                        <input
                                            type="radio"
                                            name={criteria.key}
                                            value={rating}
                                            checked={
                                                surveyData[criteria.key] ===
                                                rating.toString()
                                            }
                                            onChange={(e) =>
                                                handleSurveyChange(
                                                    criteria.key,
                                                    e.target.value
                                                )
                                            }
                                            disabled={!isEditingAllowed}
                                            className={`w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2 ${!isEditingAllowed ? "cursor-not-allowed opacity-50" : ""}`}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Star Rating Section */}
            <div className="bg-white rounded-lg md:rounded-2xl shadow-xl p-3 md:p-8 mb-4 md:mb-8 border border-gray-200">
                <div className="mb-3 md:mb-6">
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 flex items-center gap-2 flex-wrap">
                        <div className="w-6 h-6 md:w-10 md:h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-md">
                            <FiStar className="w-3 h-3 md:w-5 md:h-5 text-white fill-white" />
                        </div>
                        <span className="text-sm md:text-xl">Overall Rating</span>
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 md:ml-14 mb-2 md:mb-0">
                        Please rate your overall satisfaction with our service
                    </p>
                </div>
                
                {/* Mobile: Stars inline with text, Desktop: Centered */}
                <div className="flex flex-col items-start md:items-center justify-start md:justify-center py-0 md:py-4">
                    <div 
                        className="flex items-center gap-1 md:space-x-3 md:mb-4"
                        onMouseLeave={() => setHoveredStar(0)}
                    >
                        {[1, 2, 3, 4, 5].map((star) => {
                            const currentRating = parseInt(surveyData.starRating) || 0;
                            const hoverRating = hoveredStar || currentRating;
                            const isFilled = star <= hoverRating;
                            const isActive = star <= currentRating;
                            
                            return (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => {
                                        if (isEditingAllowed) {
                                            // If clicking the same star, set to 0, otherwise set to the star number
                                            const newRating = currentRating === star ? 0 : star;
                                            handleSurveyChange("starRating", newRating.toString());
                                            setHoveredStar(0);
                                        }
                                    }}
                                    disabled={!isEditingAllowed}
                                    onMouseEnter={() => {
                                        if (isEditingAllowed) {
                                            setHoveredStar(star);
                                        }
                                    }}
                                    className={`group relative transition-all duration-300 ${
                                        !isEditingAllowed 
                                            ? "cursor-not-allowed opacity-50" 
                                            : "cursor-pointer hover:scale-125 active:scale-110"
                                    }`}
                                >
                                    <FiStar
                                        className={`w-4 h-4 md:w-12 md:h-12 transition-all duration-300 ${
                                            isFilled
                                                ? isActive
                                                    ? "text-yellow-500 fill-yellow-500 drop-shadow-lg"
                                                    : "text-yellow-300 fill-yellow-300 drop-shadow-md"
                                                : "text-gray-300 group-hover:text-gray-400"
                                        } ${!isEditingAllowed ? "" : "group-hover:scale-110"}`}
                                    />
                                    {isActive && (
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <div className="w-4 h-4 md:w-12 md:h-12 bg-yellow-400 rounded-full opacity-20 animate-ping"></div>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/* Comments Section */}
            <div className="mb-6">
                <PosFieldLabel className="block text-sm font-medium text-gray-700 mb-2">
                    Please tell us your comments or suggestion in order to
                    improve our service to you:
                </PosFieldLabel>
                <textarea
                    value={surveyData.comments}
                    onChange={(e) =>
                        handleSurveyChange("comments", e.target.value)
                    }
                    rows={4}
                    disabled={!isEditingAllowed}
                    className={`w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isEditingAllowed ? "bg-gray-100 cursor-not-allowed" : ""}`}
                    placeholder="Your comments and suggestions..."
                />
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <PosFieldLabel className="block text-sm font-medium text-gray-700 mb-1">
                        Confirmation Order No:
                    </PosFieldLabel>
                    <input
                        type="text"
                        value={surveyData.orderNumber}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        placeholder="Order Number"
                    />
                </div>
                <div>
                    <PosFieldLabel className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Name:
                    </PosFieldLabel>
                    <input
                        type="text"
                        value={surveyData.customerName}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        placeholder="Customer Name"
                    />
                </div>
                <div className="md:col-span-2">
                    <PosFieldLabel className="block text-sm font-medium text-gray-700 mb-1">
                        Address:
                    </PosFieldLabel>
                    <input
                        type="text"
                        value={formattedAddress || "N/A"}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        placeholder="Customer Address"
                    />
                </div>
                {surveyData.date && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date:
                        </label>
                        <input
                            type="text"
                            value={moment(surveyData.date).format("DD MMM YYYY")}
                            readOnly
                            className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                )}
                {surveyData.time && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time:
                        </label>
                        <input
                            type="text"
                            value={surveyData.time}
                            readOnly
                            className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                )}
            </div>

            {/* Signature Section */}
            <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                    <PosFieldLabel className="block text-sm font-medium text-gray-700">
                        Signature:
                    </PosFieldLabel>
                    {isEditingAllowed && (
                        <button
                            type="button"
                            onClick={clearSignature}
                            className="inline-flex items-center gap-2 px-3 py-1 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        >
                            Clear Signature
                        </button>
                    )}
                </div>
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                    <canvas
                        ref={(ref) => setSignatureRef(ref)}
                        width={typeof window !== 'undefined' && window.innerWidth <= 768 ? 600 : 400}
                        height={typeof window !== 'undefined' && window.innerWidth <= 768 ? 300 : 150}
                        className={`border border-gray-400 bg-white rounded w-full ${isEditingAllowed ? "cursor-crosshair" : "cursor-not-allowed opacity-70"}`}
                        style={{ 
                            maxWidth: "100%", 
                            height: "auto",
                            minHeight: typeof window !== 'undefined' && window.innerWidth <= 768 ? "300px" : "150px",
                            pointerEvents: isEditingAllowed ? "auto" : "none",
                            touchAction: "none"
                        }}
                        onMouseDown={(e) => {
                            const canvas = e.target;
                            const rect = canvas.getBoundingClientRect();
                            const ctx = canvas.getContext("2d");
                            let isDrawing = false;

                            const startDrawing = (e) => {
                                isDrawing = true;
                                const x =
                                    (e.clientX - rect.left) *
                                    (canvas.width / rect.width);
                                const y =
                                    (e.clientY - rect.top) *
                                    (canvas.height / rect.height);
                                ctx.beginPath();
                                ctx.moveTo(x, y);
                            };

                            const draw = (e) => {
                                if (!isDrawing) return;
                                const x =
                                    (e.clientX - rect.left) *
                                    (canvas.width / rect.width);
                                const y =
                                    (e.clientY - rect.top) *
                                    (canvas.height / rect.height);
                                ctx.lineWidth = 2;
                                ctx.lineCap = "round";
                                ctx.strokeStyle = "#000";
                                ctx.lineTo(x, y);
                                ctx.stroke();
                                ctx.beginPath();
                                ctx.moveTo(x, y);
                            };

                            const stopDrawing = () => {
                                if (isDrawing) {
                                    isDrawing = false;
                                    handleSignatureEnd();
                                }
                            };

                            startDrawing(e);

                            const handleMouseMove = (e) => draw(e);
                            const handleMouseUp = () => {
                                stopDrawing();
                                document.removeEventListener(
                                    "mousemove",
                                    handleMouseMove
                                );
                                document.removeEventListener(
                                    "mouseup",
                                    handleMouseUp
                                );
                            };

                            document.addEventListener(
                                "mousemove",
                                handleMouseMove
                            );
                            document.addEventListener("mouseup", handleMouseUp);
                        }}
                        onTouchStart={(e) => {
                            e.preventDefault();
                            const canvas = e.target;
                            const rect = canvas.getBoundingClientRect();
                            const ctx = canvas.getContext("2d");
                            const touch = e.touches[0];
                            let isDrawing = false;

                            const startDrawing = (touch) => {
                                isDrawing = true;
                                const x =
                                    (touch.clientX - rect.left) *
                                    (canvas.width / rect.width);
                                const y =
                                    (touch.clientY - rect.top) *
                                    (canvas.height / rect.height);
                                ctx.beginPath();
                                ctx.moveTo(x, y);
                            };

                            const draw = (touch) => {
                                if (!isDrawing) return;
                                const x =
                                    (touch.clientX - rect.left) *
                                    (canvas.width / rect.width);
                                const y =
                                    (touch.clientY - rect.top) *
                                    (canvas.height / rect.height);
                                ctx.lineWidth = 2;
                                ctx.lineCap = "round";
                                ctx.strokeStyle = "#000";
                                ctx.lineTo(x, y);
                                ctx.stroke();
                                ctx.beginPath();
                                ctx.moveTo(x, y);
                            };

                            const stopDrawing = () => {
                                if (isDrawing) {
                                    isDrawing = false;
                                    handleSignatureEnd();
                                }
                            };

                            startDrawing(touch);

                            const handleTouchMove = (e) => {
                                e.preventDefault();
                                if (e.touches[0]) draw(e.touches[0]);
                            };
                            const handleTouchEnd = (e) => {
                                e.preventDefault();
                                stopDrawing();
                                canvas.removeEventListener(
                                    "touchmove",
                                    handleTouchMove
                                );
                                canvas.removeEventListener(
                                    "touchend",
                                    handleTouchEnd
                                );
                            };

                            canvas.addEventListener("touchmove", handleTouchMove);
                            canvas.addEventListener("touchend", handleTouchEnd);
                        }}
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                        Draw your signature above using your mouse or touch
                    </p>
                </div>
            </div>

            {/* Review Screenshots Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Facebook Review */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                                <FaFacebook className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Facebook Review</h3>
                        </div>
                        {surveyData.facebookReviewUrl && (
                            <div className="flex items-center gap-2">
                                {getReviewStatusBadge(orderData?.survey?.facebookReview?.status)}
                                {staff && orderData?.survey?.facebookReview?.status === "pending" && (
                                    <div className="flex gap-1 ml-2">
                                        <button
                                            type="button"
                                            onClick={() => handleReviewStatusChange("facebook", "approved")}
                                            className="p-1.5 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors"
                                            title="Approve"
                                        >
                                            <FiCheck className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleReviewStatusChange("facebook", "rejected")}
                                            className="p-1.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                                            title="Reject"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {surveyData.facebookReviewUrl ? (
                        <div className="relative">
                            <img 
                                src={surveyData.facebookReviewUrl} 
                                alt="Facebook Review" 
                                className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                            />
                            {isEditingAllowed && orderData?.survey?.facebookReview?.status !== "approved" && (
                                <button
                                    type="button"
                                    onClick={() => facebookInputRef.current?.click()}
                                    disabled={isUploadingFacebook}
                                    className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 text-gray-700 text-sm rounded-lg shadow hover:bg-white transition-colors flex items-center gap-1"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    Replace Image
                                </button>
                            )}
                        </div>
                    ) : (
                        <div 
                            onClick={() => isEditingAllowed && !isUploadingFacebook && facebookInputRef.current?.click()}
                            className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center ${isEditingAllowed ? 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/50' : 'cursor-not-allowed opacity-50'} transition-colors ${isUploadingFacebook ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isUploadingFacebook ? (
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mb-2" />
                                    <span className="text-gray-500">Uploading...</span>
                                </div>
                            ) : (
                                <>
                                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Upload Image</p>
                                    <p className="text-xs text-gray-400 mt-1">Upload a screenshot of your review</p>
                                </>
                            )}
                        </div>
                    )}
                    <input
                        ref={facebookInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!isEditingAllowed || orderData?.survey?.facebookReview?.status === "approved"}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            const isReplacement = !!surveyData.facebookReviewUrl;
                            if (file) handleReviewImageUpload(file, "facebook", isReplacement);
                            e.target.value = "";
                        }}
                    />
                </div>

                {/* Google Review */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                                <FaGoogle className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Google Review</h3>
                        </div>
                        {surveyData.googleReviewUrl && (
                            <div className="flex items-center gap-2">
                                {getReviewStatusBadge(orderData?.survey?.googleReview?.status)}
                                {staff && orderData?.survey?.googleReview?.status === "pending" && (
                                    <div className="flex gap-1 ml-2">
                                        <button
                                            type="button"
                                            onClick={() => handleReviewStatusChange("google", "approved")}
                                            className="p-1.5 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors"
                                            title="Approve"
                                        >
                                            <FiCheck className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleReviewStatusChange("google", "rejected")}
                                            className="p-1.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                                            title="Reject"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {surveyData.googleReviewUrl ? (
                        <div className="relative">
                            <img 
                                src={surveyData.googleReviewUrl} 
                                alt="Google Review" 
                                className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                            />
                            {isEditingAllowed && orderData?.survey?.googleReview?.status !== "approved" && (
                                <button
                                    type="button"
                                    onClick={() => googleInputRef.current?.click()}
                                    disabled={isUploadingGoogle}
                                    className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 text-gray-700 text-sm rounded-lg shadow hover:bg-white transition-colors flex items-center gap-1"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    Replace Image
                                </button>
                            )}
                        </div>
                    ) : (
                        <div 
                            onClick={() => isEditingAllowed && !isUploadingGoogle && googleInputRef.current?.click()}
                            className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center ${isEditingAllowed ? 'cursor-pointer hover:border-red-400 hover:bg-red-50/50' : 'cursor-not-allowed opacity-50'} transition-colors ${isUploadingGoogle ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {isUploadingGoogle ? (
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mb-2" />
                                    <span className="text-gray-500">Uploading...</span>
                                </div>
                            ) : (
                                <>
                                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Upload Image</p>
                                    <p className="text-xs text-gray-400 mt-1">Upload a screenshot of your review</p>
                                </>
                            )}
                        </div>
                    )}
                    <input
                        ref={googleInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!isEditingAllowed || orderData?.survey?.googleReview?.status === "approved"}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            const isReplacement = !!surveyData.googleReviewUrl;
                            if (file) handleReviewImageUpload(file, "google", isReplacement);
                            e.target.value = "";
                        }}
                    />
                </div>
            </div>

            {/* Save & Generate PDF Buttons */}
            <div className="mt-12 flex justify-center gap-4">
                {(orderData?.status !== "completed" || isRegularUser || superadmin || staff) && (
                    <button
                        onClick={() => saveSurvey()}
                        disabled={isSavingSurvey}
                        className={`group px-8 py-4 text-white rounded-2xl shadow-xl hover:shadow-2xl flex items-center gap-3 transform transition-all duration-200 ${
                            isSavingSurvey
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 hover:scale-105"
                        }`}
                    >
                        {isSavingSurvey ? (
                            <>
                                <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full"></div>
                                <span className="font-semibold text-lg">
                                    Saving...
                                </span>
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="h-6 w-6 group-hover:animate-pulse" />
                                <span className="font-semibold text-lg">
                                    Save
                                </span>
                            </>
                        )}
                    </button>
                )}
                <button
                    onClick={() => generateSurveyPDFOnly()}
                    disabled={isSavingSurvey}
                    className={`group px-8 py-4 text-white rounded-2xl shadow-xl hover:shadow-2xl flex items-center gap-3 transform transition-all duration-200 ${
                        isSavingSurvey
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-105"
                    }`}
                >
                    <FiFileText className="h-6 w-6 group-hover:animate-bounce" />
                    <span className="font-semibold text-lg">
                        Generate PDF
                    </span>
                </button>
            </div>
        </div>
    );
};

export default SurveyTab;
